'use client'

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode
} from 'react'
import {
  IconBookmark,
  IconCheck,
  IconCircleCheck,
  IconEye,
  IconTarget
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useSavedGrammarExampleCount } from '@/hooks/useContentFamiliarity'
import { useAuth } from '@/hooks/useAuth'
import {
  useLearningState,
  useLearningStates,
  useSetLearningState,
  useUpsertLearningState
} from '@/hooks/useLearningState'
import {
  getGrammarProgressDisplay,
  getStoryProgressDisplay
} from '@/lib/content-progress-core'
import {
  grammarLearningLocator,
  learningContentKey,
  storyLearningLocator
} from '@/lib/learning-state-core'
import type { LearningContentState } from '@/lib/learning-state'
import type { LearningStatus } from '@/lib/learning-state-core'
import { isSentenceSaved } from '@/lib/study'
import {
  saveGrammarExampleToDefaultDeck,
  scriptTextValue
} from '@/lib/save-to-study'
import type { GrammarExample } from '@/content/grammar/schema'

const GrammarIndexProgressContext = createContext<
  Map<string, LearningContentState>
>(new Map())

export function GrammarIndexProgressProvider({
  conceptIds,
  children
}: {
  conceptIds: string[]
  children: ReactNode
}) {
  const locators = useMemo(
    () => conceptIds.map((conceptId) => grammarLearningLocator(conceptId)),
    [conceptIds]
  )
  const { data: states = new Map() } = useLearningStates(locators)

  return (
    <GrammarIndexProgressContext.Provider value={states}>
      {children}
    </GrammarIndexProgressContext.Provider>
  )
}

export function GrammarIndexStatusBadge({ conceptId }: { conceptId: string }) {
  const states = useContext(GrammarIndexProgressContext)
  const state = states.get(
    learningContentKey(grammarLearningLocator(conceptId))
  )
  const display = getGrammarProgressDisplay(state)

  if (!state || display.kind === 'new') return null

  return <Badge variant={display.badgeVariant}>{display.label}</Badge>
}

export function GrammarProgressControl({
  conceptId,
  title
}: {
  conceptId: string
  title: string
}) {
  const { user } = useAuth()
  const locator = useMemo(() => grammarLearningLocator(conceptId), [conceptId])
  const { data: state, isLoading: stateLoading } = useLearningState(locator)
  const { data: savedExampleCount = 0 } = useSavedGrammarExampleCount(conceptId)
  const { mutateAsync: markSeenPassively, isPending: passivePending } =
    useUpsertLearningState()
  const setLearningState = useSetLearningState()
  const isPending = passivePending || setLearningState.isPending

  useEffect(() => {
    if (!user?.id) return
    if (stateLoading) return
    if (isPending) return
    if (state?.status && state.status !== 'new') return
    void markSeenPassively({
      ...locator,
      status: 'seen',
      metadata: { title }
    })
  }, [
    isPending,
    locator,
    markSeenPassively,
    state?.status,
    stateLoading,
    title,
    user?.id
  ])

  if (!user?.id) {
    return (
      <LoginDialog>
        <Button variant="outline" className="w-full justify-start">
          <IconTarget className="h-4 w-4" />
          Track progress
        </Button>
      </LoginDialog>
    )
  }

  const display = getGrammarProgressDisplay(state)
  const activeKind = display.kind
  const statusOptions: Array<{
    status: Extract<LearningStatus, 'seen' | 'studying' | 'learned'>
    label: string
    icon: typeof IconEye
  }> = [
    { status: 'seen', label: 'Seen', icon: IconEye },
    { status: 'studying', label: 'Studying', icon: IconTarget },
    { status: 'learned', label: 'Learned', icon: IconCircleCheck }
  ]

  const updateStatus = (
    status: Extract<LearningStatus, 'seen' | 'studying' | 'learned'>
  ) => {
    setLearningState.mutate(
      {
        ...locator,
        status,
        progress: status === 'learned' ? 1 : null,
        metadata: { title }
      },
      {
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Could not update grammar progress'
          )
        }
      }
    )
  }

  return (
    <div className="bg-muted/25 space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">Grammar progress</p>
          <p className="text-muted-foreground mt-1 text-xs">
            {savedExampleCount} saved example
            {savedExampleCount === 1 ? '' : 's'}
          </p>
        </div>
        <Badge variant={display.badgeVariant}>
          {stateLoading ? 'Loading' : display.label}
        </Badge>
      </div>
      <div className="bg-muted/45 grid grid-cols-3 gap-1 rounded-lg p-1">
        {statusOptions.map((option) => {
          const Icon = option.icon
          const selected = activeKind === option.status
          return (
            <Button
              key={option.status}
              variant={selected ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              disabled={isPending || stateLoading || selected}
              aria-label={`Mark grammar as ${option.label}`}
              onClick={() => updateStatus(option.status)}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{option.label}</span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export function GrammarExampleSaveButton({
  conceptId,
  grammarTitle,
  example
}: {
  conceptId: string
  grammarTitle: string
  example: GrammarExample
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const sentenceText = scriptTextValue(example.zh)
  const { data: saved = false, isLoading } = useQuery({
    queryKey: ['sentence-saved', user?.id, sentenceText],
    queryFn: () => {
      if (!user?.id) return false
      return isSentenceSaved(sentenceText)
    },
    enabled: Boolean(user?.id && sentenceText),
    staleTime: 1000 * 60 * 5
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not logged in')
      return saveGrammarExampleToDefaultDeck(user.id, {
        conceptId,
        grammarTitle,
        example
      })
    },
    onSuccess: () => {
      toast.success('Example saved to Sentence Mining')
      queryClient.setQueryData(['sentence-saved', user?.id, sentenceText], true)
      queryClient.invalidateQueries({ queryKey: ['sentence-saved', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['grammar-saved-example-count', user?.id, conceptId]
      })
      queryClient.invalidateQueries({
        queryKey: ['path-story-readiness', user?.id]
      })
      queryClient.invalidateQueries({ queryKey: ['story-recommendations'] })
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save example'
      )
    }
  })

  if (!user?.id) {
    return (
      <LoginDialog>
        <Button variant="outline" size="sm">
          <IconBookmark className="h-4 w-4" />
          Save example
        </Button>
      </LoginDialog>
    )
  }

  return (
    <Button
      variant={saved ? 'outline' : 'secondary'}
      size="sm"
      disabled={isLoading || saved || mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {saved ? (
        <IconCheck className="h-4 w-4" />
      ) : (
        <IconBookmark className="h-4 w-4" />
      )}
      {mutation.isPending ? 'Saving...' : saved ? 'Saved' : 'Save example'}
    </Button>
  )
}

export function GrammarStoryProgressBadge({
  storySlug
}: {
  storySlug: string
}) {
  const { user } = useAuth()
  const locator = useMemo(() => storyLearningLocator(storySlug), [storySlug])
  const { data: state } = useLearningState(locator)

  if (!user?.id || !state) return null

  const display = getStoryProgressDisplay(state)

  if (display?.kind === 'read') return <Badge variant="success">Read</Badge>
  if (display?.kind === 'started')
    return <Badge variant="secondary">In progress</Badge>

  return null
}
